import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, allVideos, creators, faqData } from '@/mocks/data';

function formatNumber(n: number): string {
  if (n >= 10000) {
    return (n / 10000).toFixed(n >= 100000 ? 0 : 1) + '万';
  }
  return n.toString();
}

export default function Home() {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  const hotVideos = allVideos.slice(0, 6);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: '蜜桃视频 - 甜蜜短视频平台首页',
        description: '蜜桃视频是一款专注甜蜜生活方式的短视频平台，汇聚美食、旅行、萌宠、情感等海量优质内容。',
        url: 'https://sweetvid.github.io/',
      })}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20minimal%20gradient%20background%20with%20soft%20peach%2C%20cream%2C%20and%20golden%20tones%2C%20abstract%20flowing%20shapes%2C%20gentle%20curves%2C%20modern%20clean%20design%2C%20light%20airy%20atmosphere%2C%20digital%20art%20style%2C%20no%20text%2C%20smooth%20gradient&width=1800&height=1200&seq=hero-bg-01&orientation=landscape"
              alt="蜜桃视频 - 甜蜜短视频平台"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/25"></div>
            <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-4">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                发现甜蜜，分享精彩
              </h1>
              <p className="text-base md:text-lg text-white/80 max-w-2xl mb-8 leading-relaxed">
                蜜桃视频汇聚美食、旅行、萌宠、情感等海量优质短视频内容，<br className="hidden sm:block" />让每一个生活瞬间都充满甜蜜与温暖。
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <button className="px-8 py-3 rounded-full bg-primary-500 text-background-50 text-sm md:text-base font-medium hover:bg-primary-600 whitespace-nowrap transition-all duration-300 cursor-pointer">
                  立即下载APP
                </button>
                <Link
                  to="/category/meishi"
                  onClick={scrollToTop}
                  className="px-8 py-3 rounded-full border-2 border-white/60 text-white text-sm md:text-base font-medium hover:bg-white/10 whitespace-nowrap transition-all duration-300"
                >
                  浏览内容
                </Link>
              </div>
              <div className="flex items-center gap-6 mt-10">
                {[
                  { num: '1000万+', label: '活跃用户' },
                  { num: '500万+', label: '优质内容' },
                  { num: '50万+', label: '创作者' },
                ].map((stat, i) => (
                  <div key={i} className="text-center">
                    <div className="text-xl md:text-2xl font-bold text-white">{stat.num}</div>
                    <div className="text-xs md:text-sm text-white/60 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Category Navigation */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                探索你感兴趣的内容
              </h2>
              <p className="text-sm md:text-base text-foreground-500 mt-3">八大热门分类，总有一个适合你</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={scrollToTop}
                  className="group relative rounded-lg overflow-hidden h-40 md:h-48 cursor-pointer"
                >
                  <img
                    src={cat.cardImage}
                    alt={cat.name}
                    className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                        <i className={`${cat.icon} text-white text-sm w-4 h-4 flex items-center justify-center`}></i>
                      </div>
                      <span className="text-white font-medium text-sm md:text-base">{cat.name}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* Hot Videos */}
          <section className="bg-background-100 py-14 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-10">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                    热门内容推荐
                  </h2>
                  <p className="text-sm text-foreground-500 mt-2">大家都在看的精彩视频</p>
                </div>
                <Link
                  to="/category/meishi"
                  onClick={scrollToTop}
                  className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 whitespace-nowrap transition-colors duration-200"
                >
                  查看全部 <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-product-shop>
                {hotVideos.map((video) => (
                  <Link
                    key={video.id}
                    to={`/article/${video.id}`}
                    onClick={scrollToTop}
                    className="group bg-background-50 rounded-lg overflow-hidden transition-all duration-300 hover:bg-background-100 cursor-pointer"
                  >
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded bg-black/70 text-white text-xs">
                        {video.duration}
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-medium text-foreground-950 text-sm md:text-base line-clamp-2 mb-2 group-hover:text-primary-500 transition-colors duration-200">
                        {video.title}
                      </h3>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <img
                            src={video.authorAvatar}
                            alt={video.author}
                            className="w-6 h-6 rounded-full object-cover"
                          />
                          <span className="text-xs text-foreground-500">{video.author}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-foreground-400">
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line w-3.5 h-3.5 flex items-center justify-center"></i>
                            {formatNumber(video.views)}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-heart-line w-3.5 h-3.5 flex items-center justify-center"></i>
                            {formatNumber(video.likes)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Creator Showcase */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                明星创作者
              </h2>
              <p className="text-sm text-foreground-500 mt-3">加入千万创作者，分享你的甜蜜生活</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
              {creators.map((creator) => (
                <div key={creator.name} className="text-center group cursor-pointer">
                  <div className="w-20 h-20 md:w-24 md:h-24 mx-auto mb-3 rounded-full overflow-hidden ring-2 ring-primary-200 ring-offset-2 ring-offset-background-50 transition-all duration-300 group-hover:ring-primary-400">
                    <img
                      src={creator.avatar}
                      alt={creator.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="font-medium text-foreground-950 text-sm mb-1">{creator.name}</h3>
                  <p className="text-xs text-foreground-500 mb-2">{creator.description}</p>
                  <p className="text-xs text-foreground-400">
                    {formatNumber(creator.followers)} 粉丝 · {creator.videos} 作品
                  </p>
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <button className="px-6 py-2.5 rounded-full border-2 border-primary-300 text-primary-600 text-sm font-medium hover:bg-primary-50 whitespace-nowrap transition-all duration-200 cursor-pointer">
                成为创作者
              </button>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="bg-background-100 py-14 md:py-20">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  常见问题
                </h2>
                <p className="text-sm text-foreground-500 mt-3">关于蜜桃视频，你可能想知道</p>
              </div>
              <div className="flex flex-col gap-3">
                {faqData.map((faq, index) => (
                  <div
                    key={index}
                    className="bg-background-50 rounded-lg overflow-hidden"
                  >
                    <button
                      className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                      onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                    >
                      <span className="font-medium text-foreground-900 text-sm md:text-base pr-4">{faq.question}</span>
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i
                          className={`text-foreground-400 transition-transform duration-300 text-lg ${
                            activeFaq === index ? 'ri-subtract-line rotate-180' : 'ri-add-line'
                          }`}
                        ></i>
                      </div>
                    </button>
                    <div
                      className={`overflow-hidden transition-all duration-300 ${
                        activeFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                      }`}
                    >
                      <p className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="relative w-full py-16 md:py-24 overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Soft%20gradient%20background%20with%20warm%20peach%20and%20cream%20flowing%20shapes%2C%20abstract%20modern%20design%2C%20gentle%20waves%2C%20light%20airy%20atmosphere%2C%20minimal%20digital%20art%2C%20no%20text&width=1800&height=800&seq=cta-bg-01&orientation=landscape"
              alt=""
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/15 via-black/10 to-black/15"></div>
            <div className="relative z-10 text-center px-4">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                加入蜜桃视频，开启你的甜蜜生活
              </h2>
              <p className="text-sm md:text-base text-white/70 mb-6">
                下载蜜桃视频APP，随时随地发现、创作、分享精彩内容
              </p>
              <button className="px-8 py-3 rounded-full bg-primary-500 text-background-50 text-sm md:text-base font-medium hover:bg-primary-600 whitespace-nowrap transition-all duration-300 cursor-pointer">
                立即下载APP
              </button>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}